import React, { lazy, Suspense } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './context/ThemeContext';
import MainLayout from './components/layout/MainLayout';
import { LoadingFallback } from './components/layout/MainLayout';

// Code split routes with dynamic imports for performance optimization
const Home = lazy(() => import('./pages/Home'));
const Electricians = lazy(() => import('./pages/Electricians'));
const OurApps = lazy(() => import('./pages/OurApps'));
const Apps = lazy(() => import('./pages/Apps'));
const About = lazy(() => import('./pages/About'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const QrSafe = lazy(() => import('./pages/QrSafe'));
const NotFound = lazy(() => import('./pages/NotFound'));

const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const isQrDomain = hostname === 'qr.e8software.com';
const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
const sendAppsToQr = !isQrDomain && !isLocalHost;

// The apps pages live on qr.e8software.com; send main-domain visitors there.
const ExternalRedirect: React.FC<{ to: string }> = ({ to }) => {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return <LoadingFallback />;
};

const App: React.FC = () => {
  const location = useLocation();

  return (
    <HelmetProvider>
      <ThemeProvider>
        <MainLayout>
          <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingFallback />}>
              <Routes location={location} key={location.pathname}>
                <Route
                  path="/"
                  element={
                    isQrDomain
                      ? <Navigate to="/qr-safe" replace />
                      : <Home />
                  }
                />
                <Route path="/electricians" element={<Electricians />} />
                <Route
                  path="/our-apps"
                  element={
                    sendAppsToQr
                      ? <ExternalRedirect to="https://qr.e8software.com/our-apps" />
                      : <OurApps />
                  }
                />
                <Route
                  path="/apps"
                  element={
                    sendAppsToQr
                      ? <ExternalRedirect to="https://qr.e8software.com/apps" />
                      : <Apps />
                  }
                />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/qr-safe" element={<QrSafe />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AnimatePresence>
        </MainLayout>
      </ThemeProvider>
    </HelmetProvider>
  );
};

export default App;